/* bb-quiz-attribution.js — carries quiz attribution from an email click to the order.
 *
 * THE GAP THIS CLOSES
 * The results email links to PDPs with ?quiz_id=…&quiz_rec=…. Until this script,
 * nothing outside the quiz page read those params, so a shopper who clicked a
 * product in their results email, landed on the PDP and bought produced an order
 * with no quiz_id at all — no `quiz_attribution` row, and the admin's quiz revenue
 * panel silently undercounted the quiz by exactly the share of orders the email
 * drove. Klaviyo still credited its flow, so the money was never lost; it just
 * wasn't in OUR numbers.
 *
 * HOW IT WORKS
 * The order webhook (worker/public.ts → recordAttribution) reads Shopify cart
 * attributes out of `note_attributes`, and a quiz_id alone is enough to write the
 * row. So all this has to do is get quiz_id onto the cart.
 *
 * It stamps the cart on PAGE LOAD rather than only on add-to-cart. That matters:
 * add-to-cart happens a dozen different ways across a live theme (ajax fetch, XHR,
 * the cart drawer, a plain <form> POST that reloads the page), and intercepting all
 * of them is a losing game. Cart attributes persist on the cart — including an
 * empty one — and survive through checkout, so stamping once on arrival covers
 * every path, including the ones that never touch JS.
 *
 * ─── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────────────
 * This file loads on EVERY page of a live store alongside GA4, Google Ads, the
 * Meta pixel, Klaviyo's onsite script and Shopify's own analytics. It is built so
 * it cannot affect any of them:
 *
 *  • It does NOT patch window.fetch or XMLHttpRequest. An earlier version wrapped
 *    both to catch add-to-cart; that put our code in the call path of every
 *    analytics beacon on the store for a marginal gain, so it was removed. We
 *    listen to the theme's own `cart:added` DOM event instead — passive, and
 *    invisible to anything else on the page.
 *  • It does NOT read, write, add, strip or rewrite UTM parameters. Klaviyo adds
 *    its own utm_source=Klaviyo/utm_medium=email to these links; GA4, Google Ads
 *    and Shopify's marketing attribution all keep reading exactly what they would
 *    have read. Our four params are namespaced `quiz_*` and are not UTMs.
 *  • It does NOT touch the URL, document.referrer, cookies, localStorage, or any
 *    global. Its entire footprint is one sessionStorage key and cart attributes.
 *  • It does NOT fire events to any analytics platform, so it cannot create a
 *    duplicate conversion anywhere. The only thing it writes is order metadata
 *    read by one D1 table that feeds one admin panel.
 *
 * With no quiz_id in the URL or session it does no work and issues no requests, so
 * for an ordinary shopper — including every paid-ad visitor — it is inert.
 */
(function () {
  'use strict';

  var KEY = 'bb_quiz_attr';
  var MAX_STAMPS = 5;        // runaway guard; a normal page view stamps once
  var MIN_GAP_MS = 1000;     // debounce, so nothing can hammer /cart/update.js

  // The quiz page does its own, richer attribution: it writes per-line quiz_rec
  // properties as each product is added, which is what makes per-product revenue
  // possible. Re-stamping the cart from here would flatten that, so on the quiz
  // page we stay out of the way entirely.
  //
  // Detect it by the section's server-rendered [data-bbq] element, NOT by
  // window.BB_QUIZ: this script loads from <head> and the quiz's from the section,
  // and deferred scripts run in document order — so the global does not exist yet
  // when we run. The markup does, because Shopify rendered it.
  try { if (document.querySelector('[data-bbq]')) return; } catch (e) {}

  function readStore() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return (v && typeof v === 'object' && v.quiz_id) ? v : null;
    } catch (e) { return null; }
  }

  function writeStore(v) {
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {}
  }

  function param(q, name) {
    var val = q.get(name);
    // Capped: these end up on the order record, so a hand-edited URL shouldn't be
    // able to push arbitrary text into it.
    return (val && val.length <= 128) ? val : null;
  }

  function fromUrl() {
    try {
      var q = new URLSearchParams(window.location.search);
      var id = param(q, 'quiz_id');
      // quiz_id is the join key. Without it there is nothing to attribute, and a
      // lone quiz_rec would write a row the dashboard can't tie back to a session.
      if (!id) return null;

      var out = { quiz_id: id };
      var visitor = param(q, 'quiz_visitor');
      var source = param(q, 'quiz_source');
      if (visitor) out.quiz_visitor = visitor;
      if (source) out.quiz_source = source;

      // ⚠️ The product they clicked is recorded as `quiz_click_rec`, NOT `quiz_rec`.
      // That is load-bearing, not a typo. The dashboard's per-product revenue has
      // a legacy path that credits a row's ENTIRE order_total to its `quiz_rec`
      // when the order carries no per-line pids — and an order added from a PDP
      // never carries per-line pids, because we only write cart-level attributes.
      // So writing `quiz_rec` here would credit a whole 3-item order to whichever
      // product happened to be clicked in the email: exactly the "one product takes
      // credit for everything" bug that was already fixed once for the quiz page.
      //
      // Under this name the webhook ignores it (it only reads quiz_id, quiz_rec,
      // quiz_source, quiz_visitor), so the order still counts in total quiz revenue
      // and simply doesn't claim a per-product split it cannot actually know. The
      // value stays on the order for a human reading it in Shopify.
      var rec = param(q, 'quiz_rec');
      if (rec) out.quiz_click_rec = rec;
      return out;
    } catch (e) { return null; }
  }

  // sessionStorage, not localStorage, and that is deliberate: attribution should
  // expire when the tab session does. A quiz_id persisting for weeks would credit
  // the quiz for unrelated purchases long after the fact — over-attribution is a
  // worse failure than under-attribution, because it's invisible.
  var attr = fromUrl();
  if (attr) writeStore(attr); else attr = readStore();
  if (!attr) return;   // ordinary shopper — nothing further runs.

  var stamps = 0;
  var lastAt = 0;
  var inFlight = false;

  function stamp() {
    if (inFlight || stamps >= MAX_STAMPS) return;
    var now = (new Date()).getTime();
    if (now - lastAt < MIN_GAP_MS) return;
    lastAt = now; stamps++; inFlight = true;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attr }),
        credentials: 'same-origin'
      })['catch'](function () {})
        .then(function () { inFlight = false; }, function () { inFlight = false; });
    } catch (e) { inFlight = false; }
  }

  stamp();

  // Re-stamp after an add-to-cart, in case an app or theme script reset the cart's
  // attributes in between. Passive listener on the event the theme already
  // dispatches (assets/bb-cart-add.js) — no interception of anyone's requests.
  // stamp() never dispatches cart:added itself, so this cannot feed back on itself,
  // and the debounce + cap above bound it regardless.
  try { document.addEventListener('cart:added', stamp); } catch (e) {}

  // Restored from the back/forward cache: the page didn't re-execute, but the cart
  // may have changed in another tab.
  try {
    window.addEventListener('pageshow', function (e) { if (e && e.persisted) stamp(); });
  } catch (e) {}
})();
